import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router';

import { AuthState_AuthStatus } from '@/shared/api/protocol-ts/api/auth/dto_auth_pb';
import {
  MIN_PASSWORD_LENGTH,
  PasswordValidationContext,
  PATHS,
} from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { InputPassword, Layout, Modal } from '@/shared/ui/';

import { authModel } from '@/entities/auth';
import { ModalID, modalModel } from '@/entities/modal';

import { CongratsModal } from '@/widgets/CongratsModal';
import { CustomPasswordError } from '@/widgets/CustomPasswordError';

import {
  NewPasswordFormPayload,
  newPasswordFormSchema,
} from './config/formConfig';
import styles from './NewPassword.module.scss';

type NewPasswordProps = {
  className?: string;
  testID?: string;
};

export const NewPassword: FC<NewPasswordProps> = (props) => {
  const { className, testID } = props;

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const confirmationToken = searchParams.get('token');

  const [passwordMinLength, setPasswordMinLength] =
    useState(MIN_PASSWORD_LENGTH);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted },
    setValue,
    getFieldState,
    trigger,
  } = useForm<NewPasswordFormPayload, PasswordValidationContext>({
    resolver: yupResolver(newPasswordFormSchema),
    mode: 'onChange',
    context: { passwordMinLength },
  });

  const openNewPasswordCongratsModalHandle = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.CongratsModal,
        data: {
          title: formatMessage({
            id: 'newPassword.congrats.title',
            defaultMessage:
              'Congrats! Your password has been successfully reset.',
          }),

          redirectTo: PATHS.signIn,
        },
      }),
    );
  };

  const onSubmit: SubmitHandler<NewPasswordFormPayload> = async (data) => {
    const { password } = data;

    try {
      if (confirmationToken) {
        const setPasswordResponse = await dispatch(
          authModel.thunks.setPassword({
            Token: confirmationToken,
            Password: password,
          }),
        ).unwrap();

        if (setPasswordResponse.Status === AuthState_AuthStatus.SUCCESSFUL) {
          openNewPasswordCongratsModalHandle();
        }
      }
    } catch (error) {
      // TODO: [1/l]  Investigate reason of this behaviour
      // We need a catch for useForm's isSubmitting to work correctly
    }
  };

  useEffect(() => {
    if (!confirmationToken) {
      return;
    }

    dispatch(authModel.thunks.getPasswordPolicy({ Token: confirmationToken }))
      .unwrap()
      .then(({ MinLength }) => setPasswordMinLength(MinLength))
      .catch(() => {
        /* ignore — the error toast reports it; the form keeps the default minimum */
      });
  }, [confirmationToken]);

  // The new minimum reaches the resolver on this render; re-check what was typed before it arrived.
  useEffect(() => {
    if (getFieldState('password').isDirty) {
      trigger('password');
    }
  }, [passwordMinLength]);

  useEffect(() => {
    trackEvent({
      eventName: 'NewPassword Page Viewed',
      isPageViewEvent: true,
    });
  }, []);

  return (
    <Layout>
      <Layout.Content>
        <Layout.Main>
          <div
            className={cn(styles.container, className)}
            data-testid={testID}
            data-section="new-password"
          >
            <Modal
              data-component="new-password-form"
              title={formatMessage({
                id: 'newPassword.title',
                defaultMessage: 'New password',
              })}
              shouldRenderCloseIconButton={false}
              isOpen
              okButtonText={formatMessage({
                id: 'newPassword.save',
                defaultMessage: 'Save',
              })}
              okButtonProps={{
                type: 'submit',
                form: 'newPasswordForm',
                loading: isSubmitting,
              }}
              cancelButtonProps={{ variant: 'tertiary' }}
              onCancel={() => navigate(PATHS.signIn)}
              shouldCloseOnOverlayClick={false}
            >
              <form
                id="newPasswordForm"
                onSubmit={handleSubmit(onSubmit)}
                className={styles.form}
              >
                <Controller
                  control={control}
                  name="password"
                  render={({
                    field: { ref, value, name, onBlur, onChange },
                    fieldState: { error },
                  }) => (
                    <>
                      <InputPassword
                        required
                        ref={ref}
                        value={value}
                        name={name}
                        inputMode="text"
                        type="password"
                        onBlur={onBlur}
                        onChange={onChange}
                        label={formatMessage({
                          id: 'newPassword.newPassword',
                          defaultMessage: 'New password',
                        })}
                        autoComplete="new-password"
                      />
                      {error && (
                        <CustomPasswordError
                          inputValue={value ?? ''}
                          minLength={passwordMinLength}
                        />
                      )}
                      {passwordMinLength < MIN_PASSWORD_LENGTH && (
                        <p className="p3">
                          {formatMessage(
                            {
                              id: 'newPassword.mfaMinimumHint',
                              defaultMessage:
                                'With {count} or more characters you can turn off two-step verification later.',
                            },
                            { count: MIN_PASSWORD_LENGTH },
                          )}
                        </p>
                      )}
                    </>
                  )}
                />

                <Controller
                  control={control}
                  name="confirmPassword"
                  render={({
                    field: { ref, value, name, onBlur, onChange },
                    fieldState: { error, isTouched },
                  }) => (
                    <InputPassword
                      required
                      ref={ref}
                      value={value}
                      name={name}
                      inputMode="text"
                      type="password"
                      onBlur={onBlur}
                      onChange={(event) => {
                        if (!isSubmitted && !isTouched) {
                          setValue(name, event.target.value);
                        } else {
                          onChange(event);
                        }
                      }}
                      label={formatMessage({
                        id: 'newPassword.confirmNewPassword',
                        defaultMessage: 'Confirm new password',
                      })}
                      error={error?.message}
                      autoComplete="new-password"
                    />
                  )}
                />
              </form>
            </Modal>
          </div>
        </Layout.Main>
      </Layout.Content>

      <CongratsModal />
    </Layout>
  );
};
